import React, { useState, useEffect } from 'react';
import { ShoppingCart, Check, X, Eye } from 'lucide-react';
import api from '../services/api';

const OnlineOrders = () => {
    const [orders, setOrders] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedOrder, setSelectedOrder] = useState<any>(null);

    useEffect(() => {
        fetchOrders();
    }, []);

    const fetchOrders = async () => {
        setLoading(true);
        try {
            const response = await api.get('/orders');
            const data = response.data || [];
            const parsedData = data.map((o: any) => {
                let parsedClientInfo = o.client_info;
                if (typeof parsedClientInfo === 'string') {
                    try {
                        parsedClientInfo = JSON.parse(parsedClientInfo);
                    } catch (e) {}
                }
                return { ...o, client_info: parsedClientInfo };
            });
            setOrders(parsedData);
        } catch (error) {
            console.error('Erreur lors de la récupération des commandes', error);
            setOrders([]);
        } finally {
            setLoading(false);
        }
    };

    const updateOrderStatus = async (orderId: number, newStatus: string) => {
        try {
            await api.put(`/orders/${orderId}`, { status: newStatus });
            setOrders(orders.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
            setSelectedOrder(null);
        } catch (error) {
            console.error('Erreur lors de la mise à jour de la commande', error);
            alert("Erreur lors de la mise à jour.");
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'pending': return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-bold">En attente</span>;
            case 'completed': return <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-bold">Validée</span>;
            case 'cancelled': return <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-bold">Annulée</span>;
            default: return <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-bold">{status}</span>;
        }
    };

    return (
        <div className="w-full">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-brand-dark flex items-center gap-2">
                    <ShoppingCart className="text-brand-primary" /> Commandes en Ligne
                </h1>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="text-xs text-gray-500 uppercase bg-gray-50 border-b">
                            <tr>
                                <th className="px-6 py-4">ID</th>
                                <th className="px-6 py-4">Client</th>
                                <th className="px-6 py-4">Produit</th>
                                <th className="px-6 py-4">Date</th>
                                <th className="px-6 py-4">Montant</th>
                                <th className="px-6 py-4">Statut</th>
                                <th className="px-6 py-4 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={7} className="px-6 py-8 text-center text-gray-500">Chargement des commandes...</td>
                                </tr>
                            ) : orders.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="px-6 py-8 text-center text-gray-500">Aucune commande trouvée.</td>
                                </tr>
                            ) : (
                                orders.map(order => (
                                    <tr key={order.id} className="border-b hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 font-bold text-gray-900">#{order.id}</td>
                                        <td className="px-6 py-4">
                                            <div className="font-medium text-gray-900">{order.client_info?.firstName || order.first_name} {order.client_info?.lastName || order.last_name}</div>
                                            <div className="text-xs text-gray-500">{order.client_info?.phone || order.user_email || 'N/A'}</div>
                                        </td>
                                        <td className="px-6 py-4 font-medium text-gray-700">{order.product_name}</td>
                                        <td className="px-6 py-4 text-gray-500">{new Date(order.created_at).toLocaleDateString()}</td>
                                        <td className="px-6 py-4 font-bold text-brand-primary">{Number(order.total_amount).toLocaleString()} FCFA</td>
                                        <td className="px-6 py-4">{getStatusBadge(order.status)}</td>
                                        <td className="px-6 py-4 text-right space-x-2">
                                            <button onClick={() => setSelectedOrder(order)} className="text-gray-400 hover:text-brand-primary transition-colors p-1" title="Voir détails">
                                                <Eye size={18} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Order Details Modal */}
            {selectedOrder && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 overflow-hidden flex flex-col">
                        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                            <h3 className="text-lg font-bold text-gray-900">Détails de la Commande #{selectedOrder.id}</h3>
                            <button onClick={() => setSelectedOrder(null)} className="text-gray-400 hover:text-gray-600">×</button>
                        </div>
                        <div className="p-6">
                            <div className="grid grid-cols-2 gap-4 mb-6">
                                <div>
                                    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Informations Client</h4>
                                    <p className="font-medium text-gray-900">{selectedOrder.client_info?.firstName || selectedOrder.first_name} {selectedOrder.client_info?.lastName || selectedOrder.last_name}</p>
                                    <p className="text-gray-600 text-sm mt-1">{selectedOrder.client_info?.email || selectedOrder.user_email}</p>
                                    <p className="text-gray-600 text-sm mt-1">{selectedOrder.client_info?.phone || 'Téléphone non renseigné'}</p>
                                    <p className="text-gray-600 text-sm mt-1">{selectedOrder.client_info?.address || 'Adresse non renseignée'}</p>
                                </div>
                                <div>
                                    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Détails Achat</h4>
                                    <p className="font-bold text-gray-900">{selectedOrder.product_name}</p>
                                    <p className="text-brand-primary font-bold text-xl mt-2">{Number(selectedOrder.total_amount).toLocaleString()} FCFA</p>
                                    <div className="mt-2">{getStatusBadge(selectedOrder.status)}</div>
                                </div>
                            </div>

                            {selectedOrder.status === 'pending' && (
                                <div className="flex gap-3 pt-4 border-t border-gray-100">
                                    <button 
                                        onClick={() => updateOrderStatus(selectedOrder.id, 'completed')}
                                        className="flex-1 py-2 bg-green-500 text-white rounded-md font-bold flex items-center justify-center gap-2 hover:bg-green-600 transition-colors"
                                    >
                                        <Check size={18} /> Valider la commande
                                    </button>
                                    <button 
                                        onClick={() => updateOrderStatus(selectedOrder.id, 'cancelled')}
                                        className="flex-1 py-2 bg-red-500 text-white rounded-md font-bold flex items-center justify-center gap-2 hover:bg-red-600 transition-colors"
                                    >
                                        <X size={18} /> Annuler
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OnlineOrders;
